import { useState, useEffect, useRef } from 'react';
import { Shield, Trash2, Loader2, Users, Upload, X, FileText, CheckCircle2, XCircle, AlertTriangle, Download } from 'lucide-react';
import { listFans, removeFan, importFans } from '../services/api';
import { useAuthStore } from '../store/authStore';
import ArtistDashboardNav from '../components/dashboard/ArtistDashboardNav';
import { useOnboardingProgress } from '../hooks/useOnboardingProgress';

interface Fan {
  userId: number;
  name: string;
  email: string;
  role: string;
  status: string;
  membershipName: string;
  membershipId: number;
  joinedAt: number;
}

interface FansPageProps {
  slug: string;
}

// ─── CSV Import Modal ───────────────────────────────────────────────────────

type ImportStage = 'upload' | 'preview' | 'importing' | 'complete';

interface ImportProgress {
  current: number;
  total: number;
  imported: number;
  failed: number;
  skipped: number;
}

interface RowResult {
  email: string;
  name: string;
  status: 'accepted' | 'rejected' | 'imported' | 'failed' | 'skipped';
  reason: string;
}

interface ParsedCSV {
  rows: { name: string; email: string }[];
  emailCol: number;
  nameCol: number;
  headers: string[];
}

function parseCSV(text: string): ParsedCSV | { error: string } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { error: 'File must contain a header row and at least one data row.' };

  // Parse header
  const headerLine = lines[0];
  const headers = headerLine.split(',').map((h) => h.trim().replace(/^["']|["']$/g, ''));

  if (headers.length > 2) {
    return { error: `CSV must contain at most 2 columns (found ${headers.length}). Please provide only name and email columns.` };
  }

  if (headers.length < 1) {
    return { error: 'CSV must contain at least 1 column.' };
  }

  // Detect which column is email and which is name
  const emailPattern = /email|e-mail|mail|correo/i;
  const namePattern = /name|nombre|nom|first|last|full/i;
  let emailCol = -1;
  let nameCol = -1;

  // First try by header name
  headers.forEach((h, i) => {
    if (emailPattern.test(h)) emailCol = i;
    if (namePattern.test(h)) nameCol = i;
  });

  // If headers didn't help, look at first data row content
  if (emailCol === -1 && lines.length >= 2) {
    const sampleRow = lines[1].split(',').map((c) => c.trim().replace(/^["']|["']$/g, ''));
    const emailCheck = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    sampleRow.forEach((val, i) => {
      if (emailCheck.test(val) && emailCol === -1) emailCol = i;
    });
  }

  // If still no email column found, reject
  if (emailCol === -1) {
    // With single column, assume it's all emails
    if (headers.length === 1) {
      emailCol = 0;
    } else {
      return { error: 'Could not detect the email column. Please ensure one column header contains "email".' };
    }
  }

  // Name column is the other one (if 2 columns)
  if (headers.length === 2 && nameCol === -1) {
    nameCol = emailCol === 0 ? 1 : 0;
  }

  // Parse data rows
  const rows: { name: string; email: string }[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map((c) => c.trim().replace(/^["']|["']$/g, ''));
    const email = cols[emailCol]?.trim() || '';
    const name = nameCol >= 0 ? (cols[nameCol]?.trim() || '') : '';
    if (email) rows.push({ name, email });
  }

  if (rows.length === 0) return { error: 'No valid data rows found in the CSV.' };

  return { rows, emailCol, nameCol, headers };
}

function ImportFansModal({
  slug,
  onClose,
  onComplete,
}: {
  slug: string;
  onClose: () => void;
  onComplete: () => void;
}) {
  const { token } = useAuthStore();
  const [stage, setStage] = useState<ImportStage>('upload');
  const [error, setError] = useState('');
  const [parsed, setParsed] = useState<ParsedCSV | null>(null);
  const [progress, setProgress] = useState<ImportProgress>({ current: 0, total: 0, imported: 0, failed: 0, skipped: 0 });
  const [rowResults, setRowResults] = useState<RowResult[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = (file: File) => {
    setError('');
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const result = parseCSV(text);
      if ('error' in result) {
        setError(result.error);
        return;
      }
      setParsed(result);
      setStage('preview');
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const startImport = async () => {
    if (!parsed) return;
    setStage('importing');
    setProgress({ current: 0, total: parsed.rows.length, imported: 0, failed: 0, skipped: 0 });
    setRowResults([]);

    try {
      const res = await importFans(slug, parsed.rows, token);
      if (!res.ok) {
        const errJson = await res.json().catch(() => null) as { error?: string } | null;
        setError(errJson?.error || `Import failed (${res.status})`);
        setStage('upload');
        return;
      }

      // Read SSE stream
      const reader = res.body?.getReader();
      if (!reader) {
        setError('No response stream');
        setStage('upload');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const events = buffer.split('\n\n');
        buffer = events.pop() || ''; // Keep incomplete event in buffer

        for (const eventBlock of events) {
          const lines = eventBlock.split('\n');
          let eventType = '';
          let eventData = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) eventType = line.slice(7);
            if (line.startsWith('data: ')) eventData = line.slice(6);
          }
          if (!eventData) continue;

          try {
            const data = JSON.parse(eventData);
            if (eventType === 'progress') {
              setProgress(data);
            } else if (eventType === 'row_result') {
              setRowResults((prev) => [...prev, data as RowResult]);
            } else if (eventType === 'complete') {
              setProgress(data);
              setStage('complete');
            }
          } catch {
            // Ignore parse errors
          }
        }
      }

      // If stream ended without explicit complete event, ensure we show results
      setStage('complete');
    } catch (err: any) {
      setError(err.message || 'Import failed');
      setStage('upload');
    }
  };

  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg glass-card p-6 overflow-y-auto max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded hover:bg-white/10 transition-colors">
          <X className="w-5 h-5 text-text-secondary" />
        </button>

        <h2 className="text-lg font-bold mb-1">Import Fans</h2>
        <p className="text-xs text-text-secondary mb-5 font-mono">Upload a CSV to import fans into your mailing list</p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 mb-4 text-sm text-red-400 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* UPLOAD STAGE */}
        {stage === 'upload' && (
          <>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4 text-xs text-text-secondary space-y-2">
              <p className="font-semibold text-white text-sm">CSV Format Instructions</p>
              <p>Your CSV should contain <strong className="text-white">1 or 2 columns</strong>:</p>
              <ul className="list-disc list-inside space-y-1 ml-1">
                <li><strong className="text-white">email</strong> — the fan's email address (required)</li>
                <li><strong className="text-white">name</strong> — the fan's name (optional)</li>
              </ul>
              <p className="pt-1">Example:</p>
              <pre className="bg-black/40 rounded px-3 py-2 font-mono text-[11px] text-electric-cyan/80 overflow-x-auto">
{`name,email
Alice Johnson,alice@example.com
Bob Smith,bob@example.com`}
              </pre>
              <p className="text-text-muted">Or just one column of email addresses — we'll detect the format automatically.</p>
            </div>

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                dragging ? 'border-electric-cyan bg-electric-cyan/5' : 'border-white/20 hover:border-white/40'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="w-8 h-8 text-text-secondary mx-auto mb-3" />
              <p className="text-sm text-text-secondary">Drag & drop your CSV file here, or <span className="text-electric-cyan">browse</span></p>
              <p className="text-xs text-text-muted mt-1">Maximum 1,000 rows • CSV only</p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                }}
              />
            </div>
          </>
        )}

        {/* PREVIEW STAGE */}
        {stage === 'preview' && parsed && (
          <>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-electric-cyan" />
                <span className="text-sm font-medium">{parsed.rows.length} rows detected</span>
              </div>
              <div className="text-xs text-text-secondary space-y-1">
                <p>Email column: <span className="text-white font-mono">{parsed.headers[parsed.emailCol]}</span></p>
                {parsed.nameCol >= 0 && (
                  <p>Name column: <span className="text-white font-mono">{parsed.headers[parsed.nameCol]}</span></p>
                )}
              </div>

              {/* Preview first 5 rows */}
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-1.5 px-2 font-mono text-text-muted">Name</th>
                      <th className="text-left py-1.5 px-2 font-mono text-text-muted">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.slice(0, 5).map((r, i) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="py-1.5 px-2 text-white">{r.name || '—'}</td>
                        <td className="py-1.5 px-2 text-text-secondary">{r.email}</td>
                      </tr>
                    ))}
                    {parsed.rows.length > 5 && (
                      <tr>
                        <td colSpan={2} className="py-1.5 px-2 text-text-muted text-center">
                          ...and {parsed.rows.length - 5} more
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-xs text-text-muted mb-4">
              Each email will be validated before import. Rejected emails will be shown with a clear reason. Accepted fans will receive an invitation email with a link to set up their My Collection profile.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => { setStage('upload'); setParsed(null); setError(''); }}
                className="flex-1 px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm hover:bg-white/10 transition-colors"
              >
                Back
              </button>
              <button
                onClick={startImport}
                className="flex-1 px-4 py-2.5 rounded-lg bg-electric-cyan text-deep-charcoal font-semibold text-sm hover:bg-electric-cyan/90 transition-colors"
              >
                Import {parsed.rows.length} Fans
              </button>
            </div>
          </>
        )}

        {/* IMPORTING STAGE */}
        {stage === 'importing' && (
          <div className="py-6">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Loader2 className="w-6 h-6 text-electric-cyan animate-spin" />
              <span className="text-sm">Processing emails…</span>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-white/10 rounded-full h-2 mb-3 overflow-hidden">
              <div
                className="h-full bg-electric-cyan rounded-full transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-text-secondary font-mono">
              <span>{progress.current} / {progress.total}</span>
              <span>{pct}%</span>
            </div>

            {/* Live counts */}
            <div className="grid grid-cols-2 gap-3 mt-5">
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-green-400">{progress.imported + progress.skipped}</p>
                <p className="text-[10px] font-mono text-green-400/70 uppercase">Accepted</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-red-400">{progress.failed}</p>
                <p className="text-[10px] font-mono text-red-400/70 uppercase">Rejected</p>
              </div>
            </div>
          </div>
        )}

        {/* COMPLETE STAGE */}
        {stage === 'complete' && (() => {
          const allFailed = (progress.imported + progress.skipped) === 0 && progress.failed > 0;
          const hasIssues = (progress.imported + progress.skipped) > 0 && progress.failed > 0;
          // allSuccess = progress.imported > 0 && progress.failed === 0

          return (
            <div className="py-4">
              <div className="flex items-center justify-center mb-6">
                {allFailed ? (
                  <XCircle className="w-10 h-10 text-red-400" />
                ) : hasIssues ? (
                  <AlertTriangle className="w-10 h-10 text-yellow-400" />
                ) : (
                  <CheckCircle2 className="w-10 h-10 text-green-400" />
                )}
              </div>
              <h3 className={`text-center text-lg font-semibold mb-2 ${allFailed ? 'text-red-400' : hasIssues ? 'text-yellow-400' : ''}`}>
                {allFailed ? 'Import Failed' : hasIssues ? 'Import Completed with Issues' : 'Import Complete'}
              </h3>
              <p className="text-center text-xs text-text-secondary mb-6">
                {allFailed
                  ? `All ${progress.total} emails failed validation`
                  : `${progress.total} emails scanned`}
              </p>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-green-400">{progress.imported + progress.skipped}</p>
                  <p className="text-[10px] font-mono text-green-400/70 uppercase">Accepted</p>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-red-400">{progress.failed}</p>
                  <p className="text-[10px] font-mono text-red-400/70 uppercase">Rejected</p>
                </div>
              </div>

              {progress.imported > 0 && (
                <p className="text-xs text-text-muted text-center mb-4">
                  Invitation emails have been sent to all newly accepted fans.
                </p>
              )}

              {allFailed && (
                <p className="text-xs text-text-muted text-center mb-4">
                  None of the provided emails passed validation. Please check the email addresses and try again.
                </p>
              )}

              {rowResults.length > 0 && (
                <button
                  onClick={() => {
                    const csvHeader = 'email,name,status,reason\n';
                    const csvRows = rowResults.map((r) =>
                      `"${r.email.replace(/"/g, '""')}","${(r.name || '').replace(/"/g, '""')}","${r.status}","${(r.reason || '').replace(/"/g, '""')}"`
                    ).join('\n');
                    const blob = new Blob([csvHeader + csvRows], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `import-results-${new Date().toISOString().slice(0, 10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm hover:bg-white/10 transition-colors flex items-center justify-center gap-2 mb-3"
                >
                  <Download className="w-4 h-4" />
                  Export Results as CSV
                </button>
              )}

              <button
                onClick={() => { onComplete(); onClose(); }}
                className="w-full px-4 py-2.5 rounded-lg bg-electric-cyan text-deep-charcoal font-semibold text-sm hover:bg-electric-cyan/90 transition-colors"
              >
                Done
              </button>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function FansPage({ slug }: FansPageProps) {
  const { token } = useAuthStore();
  const onboardingProgress = useOnboardingProgress(slug);
  const [fans, setFans] = useState<Fan[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [showImport, setShowImport] = useState(false);

  const loadFans = async () => {
    setLoading(true);
    try {
      const query = statusFilter !== 'All' ? `?status=${statusFilter}` : '';
      const res = await listFans(slug, query);
      if (res.ok) {
        const json = await res.json() as { data: Fan[] };
        setFans(json.data);
      }
    } catch (err) {
      console.error('[Fans] Failed to load fans:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFans();
  }, [slug, statusFilter]);

  const handleDelete = async (userId: number, name: string) => {
    if (!confirm(`Are you sure you want to remove "${name}" from your fans? This action cannot be undone.`)) return;
    setDeleting(userId);
    try {
      const res = await removeFan(slug, userId, token);
      if (res.ok) {
        setFans((prev) => prev.filter((f) => f.userId !== userId));
      }
    } catch (err) {
      console.error('[Fans] Failed to delete fan:', err);
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (ts: number): string => {
    return new Date(ts * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      Active: 'bg-green-500/20 text-green-400 border-green-500/30',
      Pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      Imported: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border ${colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-charcoal">
      <ArtistDashboardNav slug={slug} currentPage="fans" onboardingProgress={onboardingProgress} />

      <main className="pt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Fans</h1>
            <p className="text-sm text-text-secondary mt-1 font-mono">All fans registered across your memberships</p>
          </div>
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-electric-cyan/10 text-electric-cyan border border-electric-cyan/30 hover:bg-electric-cyan/20 transition-colors text-sm font-medium"
          >
            <Upload className="w-4 h-4" />
            Import Fans
          </button>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 mb-6">
          <span className="text-xs text-text-secondary font-mono uppercase tracking-wider">Filter:</span>
          {(['All', 'Active', 'Pending', 'Imported'] as const).map((filter) => (
            <button
              key={filter}
              onClick={() => setStatusFilter(filter)}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider transition-all ${
                statusFilter === filter
                  ? 'bg-electric-cyan/10 text-electric-cyan border border-electric-cyan/30'
                  : 'bg-white/5 text-text-secondary border border-white/10 hover:border-white/20 hover:text-white'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>

        {/* Table */}
        {fans.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Users className="w-12 h-12 text-electric-cyan/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No fans yet</h3>
            <p className="text-text-secondary text-sm mb-6">
              {statusFilter !== 'All'
                ? `No fans with "${statusFilter}" status found.`
                : 'When fans register through your captures, they will appear here.'}
            </p>
            {statusFilter === 'All' && (
              <button
                onClick={() => setShowImport(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-electric-cyan/10 text-electric-cyan border border-electric-cyan/30 hover:bg-electric-cyan/20 transition-colors text-sm font-medium"
              >
                <Upload className="w-4 h-4" />
                Import Fans from CSV
              </button>
            )}
          </div>
        ) : (
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Status</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Name</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Email</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Role</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Membership</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Joined</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fans.map((fan) => (
                    <tr
                      key={`${fan.userId}-${fan.membershipId}`}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    >
                      <td className="px-4 py-3">
                        {getStatusBadge(fan.status)}
                      </td>
                      <td className="px-4 py-3 font-medium text-white">
                        {fan.name}
                      </td>
                      <td className="px-4 py-3 text-xs text-text-secondary">
                        {fan.email}
                      </td>
                      <td className="px-4 py-3">
                        {fan.role === 'artist' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-purple-500/20 text-purple-300 border border-purple-500/30">
                            Artist
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-white/5 text-text-secondary border border-white/10">
                            Fan
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-electric-cyan/80">
                        {fan.membershipName}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-text-secondary whitespace-nowrap">
                        {formatDate(fan.joinedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDelete(fan.userId, fan.name)}
                          disabled={deleting === fan.userId}
                          className="p-1.5 rounded hover:bg-red-500/10 transition-colors"
                          title="Remove fan"
                        >
                          {deleting === fan.userId ? (
                            <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
                          ) : (
                            <Trash2 className="w-4 h-4 text-text-muted hover:text-red-400" />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Import Modal */}
      {showImport && (
        <ImportFansModal
          slug={slug}
          onClose={() => setShowImport(false)}
          onComplete={loadFans}
        />
      )}
    </div>
  );
}
